import { Client } from '@microsoft/microsoft-graph-client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Initialize Graph client
export const getGraphClient = (msalInstance, account) => {
  const authProvider = {
    getAccessToken: async () => {
      try {
        // First try to get token silently
        const response = await msalInstance.acquireTokenSilent({
          scopes: ['User.Read', 'User.Read.All', 'GroupMember.Read.All', 'Group.Read.All', 'Directory.Read.All'],
          account: account,
          forceRefresh: false,
        });
        return response.accessToken;
      } catch (error) {
        console.error('Silent token acquisition failed:', error);
        
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await msalInstance.acquireTokenPopup({
              scopes: ['User.Read', 'User.Read.All', 'GroupMember.Read.All', 'Group.Read.All', 'Directory.Read.All'],
              account: account,
            });
            return response.accessToken;
          } catch (popupError) {
            console.error('Popup token acquisition failed:', popupError);
            throw popupError;
          }
        }
        throw error;
      }
    },
  };

  return Client.initWithMiddleware({ authProvider });
};

// Note: The MSAL instance and account should be passed from the React components
// that have access to the useMsal hook

// Get members of SEM Team group
export const getSEMTeamMembers = async (graphClient) => {
  try {
    const groupId = '21c35de9-acc8-4d56-83a9-be2c1e91775e';
    // Get group members
    const response = await graphClient
      .api(`/groups/${groupId}/members`)
      .select('id,displayName,mail,userPrincipalName')
      .top(999)
      .get();
    
    // Process the response - we see users but with null properties
    // So we need to fetch each user individually
    const userIds = response.value
      .filter(member => member['@odata.type'] === '#microsoft.graph.user')
      .map(member => member.id);
    
    let members = [];

    if (userIds.length > 0) {
      const userPromises = userIds.map(async (userId) => {
        try {
          const userDetails = await graphClient
            .api(`/users/${userId}`)
            .select('id,displayName,mail,userPrincipalName,givenName,surname')
            .get();
          
          return {
            id: userDetails.id,
            displayName: userDetails.displayName || `${userDetails.givenName || ''} ${userDetails.surname || ''}`.trim() || 'Unknown User',
            email: userDetails.mail || userDetails.userPrincipalName || 'no-email@acom.edu.au',
          };
        } catch (userErr) {
          console.error(`Failed to fetch user ${userId}:`, userErr);
          return null;
        }
      });
      
      const results = await Promise.all(userPromises);
      members = results.filter(m => m !== null && m.email && m.email !== 'no-email@acom.edu.au');
    }
    
    if (members.length === 0) {
      throw new Error('No members could be retrieved - check permissions');
    }
    
    return members;
  } catch (error) {
    console.error('Error fetching SEM Team members:', error);
    
    // Return fallback data on API error
    return [
      { id: '1', displayName: 'Sarah Mitchell', email: 'sarah.mitchell@acom.edu.au' },
      { id: '2', displayName: 'James Wilson', email: 'james.wilson@acom.edu.au' },
      { id: '3', displayName: 'Emma Thompson', email: 'emma.thompson@acom.edu.au' },
    ];
  }
};

// Get members of LR_Approvers group (Leave Request Approvers)
export const getLRApprovers = async (graphClient) => {
  try {
    const groupId = APPROVER_GROUPS.LR_APPROVERS;
    // Get group members
    const response = await graphClient
      .api(`/groups/${groupId}/members`)
      .select('id,displayName,mail,userPrincipalName')
      .top(999)
      .get();

    // Process the response
    const userIds = response.value
      .filter(member => member['@odata.type'] === '#microsoft.graph.user')
      .map(member => member.id);

    // Fetch individual user details
    const users = await Promise.all(
      userIds.map(async (userId) => {
        try {
          const user = await graphClient
            .api(`/users/${userId}`)
            .select('id,displayName,mail,userPrincipalName')
            .get();
          return user;
        } catch (err) {
          console.error(`Error fetching user ${userId}:`, err);
          return null;
        }
      })
    );

    const validUsers = users.filter(u => u && u.mail);
    return validUsers;
  } catch (error) {
    console.error('Error fetching LR Approvers:', error);
    throw error;
  }
};

// Get members of ER_Approvers group
export const getERApprovers = async (graphClient) => {
  try {
    const groupId = APPROVER_GROUPS.ER_APPROVERS;
    // Get group members
    const response = await graphClient
      .api(`/groups/${groupId}/members`)
      .select('id,displayName,mail,userPrincipalName')
      .top(999)
      .get();
    
    // Process the response - we see users but with null properties
    // So we need to fetch each user individually
    const userIds = response.value
      .filter(member => member['@odata.type'] === '#microsoft.graph.user')
      .map(member => member.id);
    
    let approvers = [];

    if (userIds.length > 0) {
      const userPromises = userIds.map(async (userId) => {
        try {
          const userDetails = await graphClient
            .api(`/users/${userId}`)
            .select('id,displayName,mail,userPrincipalName,givenName,surname')
            .get();
          
          return {
            id: userDetails.id,
            displayName: userDetails.displayName || `${userDetails.givenName || ''} ${userDetails.surname || ''}`.trim() || 'Unknown User',
            email: userDetails.mail || userDetails.userPrincipalName || 'no-email@acom.edu.au',
          };
        } catch (userErr) {
          console.error(`Failed to fetch approver ${userId}:`, userErr);
          return null;
        }
      });
      
      const results = await Promise.all(userPromises);
      approvers = results.filter(m => m !== null && m.email && m.email !== 'no-email@acom.edu.au');
    }

    // Ensure Angela Sawyer is in the list and set as default
    const angelaSawyer = approvers.find(a => 
      a.displayName?.toLowerCase().includes('angela sawyer') ||
      a.email?.toLowerCase().includes('angela.sawyer')
    );

    if (!angelaSawyer) {
      approvers.unshift({
        id: 'angela-default',
        displayName: 'Angela Sawyer',
        email: 'asawyer@acom.edu.au',
      });
    }

    if (approvers.length === 0) {
      throw new Error('No approvers could be retrieved');
    }
    
    return approvers;
  } catch (error) {
    console.error('Error fetching ER Approvers');

    // Return fallback data on API error
    return [
      { id: 'angela-default', displayName: 'Angela Sawyer', email: 'asawyer@acom.edu.au' },
      { id: '2', displayName: 'Michael Brown', email: 'michael.brown@acom.edu.au' },
      { id: '3', displayName: 'Jennifer Davis', email: 'jennifer.davis@acom.edu.au' },
      { id: '4', displayName: 'Robert Wilson', email: 'robert.wilson@acom.edu.au' },
    ];
  }
};

// Group IDs for different approver groups
export const APPROVER_GROUPS = {
  ER_APPROVERS: 'ff5f5e4e-6dd1-4938-8d66-59deb1b2e303',
  TR_APPROVERS: '3aaee08d-789a-4f8a-b783-e9bb4528f674',
  LR_APPROVERS: 'd5c4736e-4825-44ad-9f43-02970211e096',
  MR_APPROVERS: 'e4806c56-b055-478c-ae42-0c19a93b88ed',
  // Add more groups here in the future
  // FINANCE_APPROVERS: 'group-id-here',
};

// Check if user is member of a specific group
export const checkUserInGroup = async (userEmail, groupId, msalInstance, account) => {
  try {
    const graphClient = getGraphClient(msalInstance, account);
    
    // Try to check membership directly
    try {
      const user = await graphClient
        .api('/me')
        .select('id,mail,userPrincipalName')
        .get();
      
      const userId = user.id;
      const isMember = await graphClient
        .api(`/groups/${groupId}/members/${userId}`)
        .get()
        .then(() => true)
        .catch(() => false);
      
      if (isMember) return true;
    } catch (e) {
      // Direct membership check failed, trying group members list
    }
    
    // Fallback to checking group members list
    const members = await getGroupMembers(graphClient, groupId);
    return members.some(member => 
      member.email?.toLowerCase() === userEmail?.toLowerCase()
    );
  } catch (error) {
    console.error('Error checking group membership:', error);
    return false;
  }
};

// Get members of any group
export const getGroupMembers = async (graphClient, groupId) => {
  try {
    const response = await graphClient
      .api(`/groups/${groupId}/members`)
      .select('id,displayName,mail,userPrincipalName')
      .top(999)
      .get();
    
    // Process members
    const members = response.value
      .filter(member => member['@odata.type'] === '#microsoft.graph.user')
      .map(member => ({
        id: member.id,
        displayName: member.displayName || member.userPrincipalName,
        email: member.mail || member.userPrincipalName
      }));
    
    return members;
  } catch (error) {
    console.error(`Error fetching group ${groupId} members:`, error);
    return [];
  }
};

// Check if current user is an approver (checks all approver groups)
export const checkIfUserIsApprover = async (userEmail, msalInstance, account) => {
  
  try {
    if (!msalInstance || !account) {
      return false;
    }
    
    // Check multiple approver groups
    const isERApprover = await checkUserInGroup(userEmail, APPROVER_GROUPS.ER_APPROVERS, msalInstance, account);
    const isTRApprover = await checkUserInGroup(userEmail, APPROVER_GROUPS.TR_APPROVERS, msalInstance, account);
    const isLRApprover = await checkUserInGroup(userEmail, APPROVER_GROUPS.LR_APPROVERS, msalInstance, account);
    const isMRApprover = await checkUserInGroup(userEmail, APPROVER_GROUPS.MR_APPROVERS, msalInstance, account);

    return isERApprover || isTRApprover || isLRApprover || isMRApprover;
  } catch (error) {
    console.error('Error checking if user is approver');
    return false;
  }
};

// Test function to verify Graph API permissions
export const testGraphPermissions = async (graphClient) => {
  const results = {
    user: false,
    groups: false,
    groupMembers: false,
    userList: false,
    specificUser: false,
  };

  try {
    await graphClient.api('/me').select('id,displayName,mail').get();
    results.user = true;
  } catch (error) { /* permission not available */ }

  try {
    await graphClient.api('/groups').top(1).get();
    results.groups = true;
  } catch (error) { /* permission not available */ }

  try {
    const testGroupId = '21c35de9-acc8-4d56-83a9-be2c1e91775e';
    await graphClient.api(`/groups/${testGroupId}/members`).select('id').top(1).get();
    results.groupMembers = true;
  } catch (error) { /* permission not available */ }

  try {
    await graphClient.api('/users').select('id,displayName,mail').top(1).get();
    results.userList = true;
  } catch (error) { /* permission not available */ }

  try {
    const testMembers = await graphClient.api(`/groups/21c35de9-acc8-4d56-83a9-be2c1e91775e/members`).select('id').top(1).get();
    if (testMembers.value && testMembers.value.length > 0) {
      const userId = testMembers.value[0].id;
      await graphClient.api(`/users/${userId}`).select('id,displayName,mail,userPrincipalName').get();
      results.specificUser = true;
    }
  } catch (error) { /* permission not available */ }

  return results;
};